import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, MessageSquare, Users, Sparkles, ArrowLeft } from "lucide-react";
import Link from "next/link";
import { formatConversationLimit, UNLIMITED_CONVERSATIONS } from "@/lib/onboarding/plans";
import { cn } from "@/lib/utils";
import { persianToEnglishNumber } from "@/utils/modules/numbers";
import { requiredExtraConversations } from "@/utils/billing";
import type { IPlan } from "@/types/billing";

const fmt = (n: number) => Math.round(n).toLocaleString("fa-IR");

const MSG_PER_CONVERSATION = 5;

type Mode = "contacts" | "messages";

export interface UsageCalculatorSelection {
  plan: IPlan;
  conversations: number;
  extra: number;
  extraCost: number;
  total: number;
  unlimited: boolean;
}

interface UsageCalculatorProps {
  plans: IPlan[];
  loggedIn?: boolean;
  onBuy?: (selection: UsageCalculatorSelection) => void;
  className?: string;
}

export default function UsageCalculator({
  plans,
  loggedIn = false,
  onBuy,
  className,
}: UsageCalculatorProps) {
  const [mode, setMode] = useState<Mode>("contacts");
  const [contacts, setContacts] = useState(900);
  const [messages, setMessages] = useState(4500);
  const [returnRate, setReturnRate] = useState(30);
  const [contactsInvalid, setContactsInvalid] = useState(false);
  const [returnRateInvalid, setReturnRateInvalid] = useState(false);
  const [messagesInvalid, setMessagesInvalid] = useState(false);
  const hasInvalidInput =
    mode === "contacts" ? contactsInvalid || returnRateInvalid : messagesInvalid;

  const switchMode = (next: Mode) => {
    setMode(next);
    setContactsInvalid(false);
    setReturnRateInvalid(false);
    setMessagesInvalid(false);
  };

  const conversations = useMemo(() => {
    if (mode === "messages") return Math.ceil(messages / MSG_PER_CONVERSATION);
    return Math.ceil(contacts * (1 + returnRate / 100));
  }, [mode, contacts, messages, returnRate]);

  const options = useMemo(() => {
    return plans
      .filter((p) => !p.is_trial)
      .map((p) => {
        const unlimited = p.conversation_limit >= UNLIMITED_CONVERSATIONS;
        const extra = unlimited
          ? 0
          : requiredExtraConversations(conversations, p.conversation_limit);
        const extraCost = extra * p.per_conversation_price;
        return { plan: p, extra, extraCost, total: p.monthly + extraCost, unlimited };
      });
  }, [conversations, plans]);

  const best = useMemo(
    () => options.reduce((m, o) => (o.total < m.total ? o : m), options[0]),
    [options],
  );

  const startHref = best
    ? `/dashboard/plan/checkout?plan=${best.plan.key}&cycle=monthly`
    : "/dashboard/pricing";

  const ctaLabel = best
    ? onBuy
      ? "افزودن مکالمه‌های اضافه به سفارش"
      : `شروع با ${best.plan.name}`
    : "";

  const ctaClasses =
    "mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-2xl bg-gradient-to-l from-primary-main to-purple px-4 py-3 text-[13px] font-black text-white shadow-sm transition";

  return (
    <section dir="rtl" className={cn("mx-auto max-w-7xl px-6 pb-16", className)}>
      <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
        <div className="border-b border-border bg-gradient-to-l from-primary-main/10 to-transparent p-5 sm:p-6">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary-main to-purple text-white shadow-sm">
              <Calculator size={17} />
            </span>
            <div>
              <h2 className="text-base font-black text-foreground sm:text-lg">ماشین‌حساب مصرف</h2>
              <p className="text-[12px] text-muted-foreground">
                تخمین هزینهٔ ماهانه بر اساس تعداد مخاطب یا پیام
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[1fr_380px]">
          <div className="space-y-5">
            <div className="inline-flex rounded-2xl border border-border bg-muted/40 p-1">
              {(
                [
                  { k: "contacts", label: "بر اساس مخاطب", icon: Users },
                  { k: "messages", label: "بر اساس پیام", icon: MessageSquare },
                ] as const
              ).map((t) => (
                <button
                  key={t.k}
                  onClick={() => switchMode(t.k)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12px] font-black transition",
                    mode === t.k
                      ? "bg-primary-main text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <t.icon size={13} /> {t.label}
                </button>
              ))}
            </div>

            {mode === "contacts" ? (
              <>
                <Slider
                  label="تعداد مخاطبان یکتا در ماه"
                  value={contacts}
                  onChange={setContacts}
                  min={50}
                  max={20000}
                  step={50}
                  suffix="نفر"
                  onInvalidChange={setContactsInvalid}
                />
                <Slider
                  label="چند درصد مخاطبان روز دیگری دوباره پیام می‌دهند؟"
                  value={returnRate}
                  onChange={setReturnRate}
                  min={0}
                  max={100}
                  step={5}
                  suffix="٪"
                  onInvalidChange={setReturnRateInvalid}
                />
              </>
            ) : (
              <Slider
                label="تعداد پیام‌هایی که ترندی پاسخ می‌دهد (ماهانه)"
                value={messages}
                onChange={setMessages}
                min={200}
                max={100000}
                step={100}
                suffix="پیام"
                onInvalidChange={setMessagesInvalid}
              />
            )}

            <div className="rounded-2xl border border-primary-main/20 bg-primary-main/5 p-4">
              <p className="text-[12px] text-muted-foreground">مکالمه‌های موردنیاز شما در ماه</p>
              <p className="mt-1 text-2xl font-black text-primary-main">
                {fmt(conversations)} مکالمه
              </p>
              <p className="mt-1 text-[11.5px] text-muted-foreground">
                مکالمه = پنجرهٔ ۲۴ ساعته با یک مخاطب؛ به‌طور میانگین هر مکالمه ≈{" "}
                {MSG_PER_CONVERSATION.toLocaleString("fa-IR")} پیام هوشمند.
              </p>
            </div>
          </div>

          <div className="space-y-2.5">
            {options.length === 0 ? (
              <p className="rounded-2xl border border-border bg-card p-4 text-center text-[12px] text-muted-foreground">
                در حال حاضر پلنی برای محاسبه در دسترس نیست.
              </p>
            ) : (
              options.map((o) => {
                const isBest = o.plan.key === best?.plan.key;
                return (
                  <motion.div
                    key={o.plan.key}
                    layout
                    className={cn(
                      "rounded-2xl border p-3.5 transition",
                      isBest
                        ? "border-primary-main bg-primary-main/5 shadow-sm"
                        : "border-border bg-card",
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-black text-foreground">
                          {o.plan.name}
                        </span>
                        {isBest && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary-main px-2 py-0.5 text-[9.5px] font-black text-white">
                            <Sparkles size={10} /> مقرون‌به‌صرفه‌ترین
                          </span>
                        )}
                      </div>
                      <span className="text-[13px] font-black text-foreground">
                        {fmt(o.total)} ت
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      <span>سهمیه: {formatConversationLimit(o.plan.conversation_limit)}</span>
                      <span>اشتراک: {fmt(o.plan.monthly)} ت</span>
                      <span>
                        {o.unlimited
                          ? "بدون مکالمهٔ اضافه"
                          : o.extra > 0
                            ? `${fmt(o.extra)} مکالمهٔ اضافه: ${fmt(o.extraCost)} ت`
                            : "بدون نیاز به بستهٔ اضافه"}
                      </span>
                    </div>
                  </motion.div>
                );
              })
            )}

            {best &&
              (hasInvalidInput ? (
                <span
                  aria-disabled="true"
                  className={cn(ctaClasses, "cursor-not-allowed opacity-60")}
                >
                  {ctaLabel}
                </span>
              ) : onBuy ? (
                <button
                  type="button"
                  onClick={() => onBuy({ ...best, conversations })}
                  className={cn(ctaClasses, "hover:shadow-lg")}
                >
                  {ctaLabel} <ArrowLeft size={15} />
                </button>
              ) : (
                <Link
                  href={loggedIn ? startHref : `/login?next=${encodeURIComponent(startHref)}`}
                  className={cn(ctaClasses, "hover:shadow-lg")}
                >
                  {ctaLabel} <ArrowLeft size={15} />
                </Link>
              ))}
            <p className="text-center text-[10.5px] text-muted-foreground">
              تخمین بر پایهٔ قیمت‌های فعلی است؛ پاسخ‌های ثابت و انتقال به اپراتور رایگان‌اند.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Slider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  suffix,
  onInvalidChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step: number;
  suffix: string;
  onInvalidChange?: (invalid: boolean) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const overMax = draft !== "" && Number(draft) > max;

  const handleTextChange = (raw: string) => {
    const digits = persianToEnglishNumber(raw).replace(/[^0-9]/g, "");
    setDraft(digits);
    if (digits === "") {
      onInvalidChange?.(true);
      return;
    }
    const parsed = Number(digits);
    if (parsed > max) {
      onInvalidChange?.(true);
      return;
    }
    onInvalidChange?.(false);
    onChange(parsed);
  };

  const handleBlur = () => {
    if (Number(draft) > max) return;
    onInvalidChange?.(false);
    setDraft(String(value));
  };

  const handleRangeChange = (raw: string) => {
    const parsed = Number(raw);
    onChange(parsed);
    setDraft(String(parsed));
    onInvalidChange?.(false);
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <label className="text-[12.5px] font-bold text-foreground">{label}</label>
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            inputMode="numeric"
            aria-label={label}
            value={draft}
            onChange={(e) => handleTextChange(e.target.value)}
            onBlur={handleBlur}
            aria-invalid={overMax}
            className={cn(
              "h-8 w-24 rounded-lg border border-border bg-background px-2 text-center text-[12px] font-black text-foreground outline-none focus:border-primary-main",
              overMax && "border-red-500",
            )}
          />
          <span className="text-[11px] text-muted-foreground">{suffix}</span>
        </div>
      </div>
      {overMax && (
        <p className="mb-2 text-[10.5px] font-bold text-red-500">
          بیشتر از {suffix === "٪" ? `${fmt(max)}٪` : `${fmt(max)} ${suffix}`} نمی‌شود
        </p>
      )}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => handleRangeChange(e.target.value)}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary-main"
        aria-label={label}
      />
    </div>
  );
}
